// CodeInterpreter.js
// Interactive Code Interpreter - Provides AI-generated coding challenges with multi-language execution
// Features: Code editor, Piston API execution, test validation, solution hints

import React, { useState, useEffect, useRef } from 'react';
import './CodeInterpreter.css';

const CodeInterpreter = ({ onClose, makeOpenAIRequest, currentTopic = "programming" }) => {
  // State management
  const [language, setLanguage] = useState('python');
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);

  // Challenge state
  const [challenge, setChallenge] = useState(null);
  const [loadingChallenge, setLoadingChallenge] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [showSolution, setShowSolution] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const codeEditorRef = useRef(null);

  // Supported languages with Piston
  const supportedLanguages = [
    { id: 'python', name: 'Python', version: '3.10.0', extension: 'py', starter: '# Write your Python code here\n\n' },
    { id: 'javascript', name: 'JavaScript', version: '18.15.0', extension: 'js', starter: '// Write your JavaScript code here\n\n' },
    { id: 'java', name: 'Java', version: '15.0.2', extension: 'java', starter: 'public class Main {\n    public static void main(String[] args) {\n        // Write your Java code here\n        \n    }\n}\n' },
    { id: 'c', name: 'C', version: '10.2.0', extension: 'c', starter: '#include <stdio.h>\n\nint main() {\n    // Write your C code here\n    \n    return 0;\n}\n' },
    { id: 'cpp', name: 'C++', version: '10.2.0', extension: 'cpp', starter: '#include <iostream>\nusing namespace std;\n\nint main() {\n    // Write your C++ code here\n    \n    return 0;\n}\n' },
    { id: 'go', name: 'Go', version: '1.16.2', extension: 'go', starter: 'package main\n\nimport "fmt"\n\nfunc main() {\n    // Write your Go code here\n    \n}\n' },
    { id: 'rust', name: 'Rust', version: '1.68.0', extension: 'rs', starter: 'fn main() {\n    // Write your Rust code here\n    \n}\n' },
    { id: 'ruby', name: 'Ruby', version: '3.0.1', extension: 'rb', starter: '# Write your Ruby code here\n\n' },
    { id: 'php', name: 'PHP', version: '8.2.3', extension: 'php', starter: '<?php\n// Write your PHP code here\n\n?>\n' }
  ];

  // Get current language config
  const getCurrentLanguage = () => supportedLanguages.find(lang => lang.id === language) || supportedLanguages[0];

  // Initialize with starter code
  useEffect(() => {
    const langConfig = getCurrentLanguage();
    if (!code) {
      setCode(langConfig.starter);
    }
  }, [language]);

  // Generate AI coding challenge
  const generateChallenge = async () => {
    setLoadingChallenge(true);
    setError(null);
    setTestResults([]);
    setShowSolution(false);
    setAttempts(0);

    try {
      const prompt = `Generate a coding challenge for learning ${currentTopic} in ${getCurrentLanguage().name}.

Return a JSON object with this exact structure:
{
  "title": "Challenge title",
  "description": "Detailed problem description with examples",
  "difficulty": "Easy|Medium|Hard",
  "testCases": [
    {"input": "test input 1", "expectedOutput": "expected output 1"},
    {"input": "test input 2", "expectedOutput": "expected output 2"},
    {"input": "test input 3", "expectedOutput": "expected output 3"}
  ],
  "hints": ["hint 1", "hint 2", "hint 3"],
  "solution": "Complete working solution code",
  "explanation": "Explanation of the solution approach"
}

Make it appropriate for beginners to intermediate level. Include at least 3 test cases.`;

      const messages = [
        {
          role: 'user',
          content: prompt
        }
      ];

      const response = await makeOpenAIRequest(messages, 2000);

      // Parse the JSON response
      let challengeData;
      try {
        // Try to extract JSON from markdown code blocks if present
        const jsonMatch = response.match(/```json?\n?([\s\S]*?)\n?```/);
        const jsonString = jsonMatch ? jsonMatch[1] : response;
        challengeData = JSON.parse(jsonString);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Failed to parse challenge data. Please try again.');
      }

      setChallenge(challengeData);
      setOutput('✨ New challenge loaded! Read the problem and start coding.');
    } catch (err) {
      console.error('Challenge generation error:', err);
      setError(err.message || 'Failed to generate challenge. Please try again.');
    } finally {
      setLoadingChallenge(false);
    }
  };

  // Execute code using Piston API
  const executeCode = async (testInput = '') => {
    if (!code.trim()) {
      setOutput('⚠️ Please write some code first!');
      return null;
    }

    setIsRunning(true);
    setError(null);

    try {
      const langConfig = getCurrentLanguage();

      const requestBody = {
        language: langConfig.id,
        version: langConfig.version,
        files: [{
          name: `main.${langConfig.extension}`,
          content: code
        }],
        stdin: testInput,
        args: [],
        compile_timeout: 10000,
        run_timeout: 3000,
        compile_memory_limit: -1,
        run_memory_limit: -1
      };

      const response = await fetch('https://emkc.org/api/v2/piston/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      return {
        stdout: result.run?.stdout || '',
        stderr: result.run?.stderr || '',
        code: result.run?.code || 0,
        signal: result.run?.signal || null
      };

    } catch (err) {
      console.error('Execution error:', err);
      setError(err.message || 'Failed to execute code. Please try again.');
      return null;
    } finally {
      setIsRunning(false);
    }
  };

  // Run code (free play mode)
  const runCode = async () => {
    setAttempts(prev => prev + 1);
    const result = await executeCode();

    if (result) {
      let outputText = '';

      if (result.stderr) {
        outputText += `❌ Error:\n${result.stderr}\n\n`;
      }

      if (result.stdout) {
        outputText += `✅ Output:\n${result.stdout}\n`;
      } else if (!result.stderr) {
        outputText += '✅ Code executed successfully with no output.\n';
      }

      if (result.code !== 0) {
        outputText += `\nExit code: ${result.code}\n`;
      }

      setOutput(outputText);
    }
  };

  // Run tests against challenge test cases
  const runTests = async () => {
    if (!challenge || !challenge.testCases) {
      setOutput('⚠️ No challenge loaded. Generate a challenge first!');
      return;
    }

    setAttempts(prev => prev + 1);
    setIsRunning(true);
    setTestResults([]);
    setOutput('🧪 Running test cases...\n\n');

    const results = [];

    for (let i = 0; i < challenge.testCases.length; i++) {
      const testCase = challenge.testCases[i];
      const result = await executeCode(testCase.input);

      if (result) {
        const actualOutput = result.stdout.trim();
        const expectedOutput = testCase.expectedOutput.trim();
        const passed = actualOutput === expectedOutput;

        results.push({
          testNumber: i + 1,
          input: testCase.input,
          expected: expectedOutput,
          actual: actualOutput,
          passed: passed,
          error: result.stderr
        });
      }
    }

    setTestResults(results);
    setIsRunning(false);

    // Generate output summary
    const passedCount = results.filter(r => r.passed).length;
    const totalCount = results.length;

    let outputText = `📊 Test Results: ${passedCount}/${totalCount} passed\n\n`;

    results.forEach(result => {
      const icon = result.passed ? '✅' : '❌';
      outputText += `${icon} Test ${result.testNumber}: ${result.passed ? 'PASSED' : 'FAILED'}\n`;
      outputText += `   Input: ${result.input || '(none)'}\n`;
      outputText += `   Expected: ${result.expected}\n`;
      outputText += `   Got: ${result.actual}\n`;
      if (result.error) {
        outputText += `   Error: ${result.error}\n`;
      }
      outputText += '\n';
    });

    if (passedCount === totalCount) {
      outputText += '🎉 Congratulations! All tests passed!\n';
    } else {
      outputText += `💡 ${totalCount - passedCount} test(s) failed. Try debugging your code or use hints.\n`;
    }

    setOutput(outputText);
  };

  // Show hint
  const showHint = () => {
    if (!challenge || !challenge.hints) {
      setOutput('⚠️ No hints available. Generate a challenge first!');
      return;
    }

    const hintIndex = Math.min(attempts, challenge.hints.length - 1);
    const hint = challenge.hints[hintIndex];

    setOutput(prevOutput => prevOutput + `\n💡 Hint ${hintIndex + 1}: ${hint}\n`);
  };

  // Show solution
  const displaySolution = () => {
    if (!challenge || !challenge.solution) {
      setOutput('⚠️ No solution available. Generate a challenge first!');
      return;
    }

    setShowSolution(true);
    setCode(challenge.solution);
    setOutput(`📖 Solution loaded into editor!\n\nExplanation:\n${challenge.explanation}\n\nYou can now run the solution to see it in action.`);
  };

  // Handle tab key in textarea
  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newValue = code.substring(0, start) + '    ' + code.substring(end);
      setCode(newValue);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 4;
      }, 0);
    }
  };

  return (
    <div className="code-interpreter-container">
      {/* Header */}
      <div className="interpreter-header">
        <div className="header-left">
          <h2>💻 Code Interpreter</h2>
          {challenge && (
            <span className="challenge-badge">{challenge.difficulty}</span>
          )}
        </div>
        <button onClick={onClose} className="close-interpreter-btn" title="Close">×</button>
      </div>

      {/* Challenge Panel */}
      {challenge && (
        <div className="challenge-panel">
          <h3>{challenge.title}</h3>
          <p className="challenge-description">{challenge.description}</p>
          {testResults.length > 0 && (
            <div className="test-summary">
              {testResults.filter(r => r.passed).length === testResults.length ? (
                <span className="all-passed">✅ All tests passed!</span>
              ) : (
                <span className="some-failed">
                  {testResults.filter(r => r.passed).length}/{testResults.length} tests passed
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="interpreter-content">
        {/* Left: Editor */}
        <div className="editor-section">
          <div className="editor-toolbar">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="language-select"
            >
              {supportedLanguages.map(lang => (
                <option key={lang.id} value={lang.id}>{lang.name}</option>
              ))}
            </select>

            <div className="toolbar-actions">
              <button
                onClick={generateChallenge}
                disabled={loadingChallenge}
                className="toolbar-btn challenge-btn"
                title="Generate new coding challenge"
              >
                {loadingChallenge ? '⏳ Generating...' : '✨ New Challenge'}
              </button>

              <button
                onClick={runCode}
                disabled={isRunning}
                className="toolbar-btn run-btn"
                title="Run code"
              >
                {isRunning ? '⏳ Running...' : '▶️ Run'}
              </button>

              {challenge && (
                <button
                  onClick={runTests}
                  disabled={isRunning}
                  className="toolbar-btn test-btn"
                  title="Run test cases"
                >
                  🧪 Test
                </button>
              )}
            </div>
          </div>

          <textarea
            ref={codeEditorRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            className="code-editor"
            spellCheck="false"
            placeholder="Write your code here..."
          />
        </div>

        {/* Right: Output */}
        <div className="output-section">
          <div className="output-toolbar">
            <span className="output-title">Console Output</span>
            {challenge && (
              <div className="hint-actions">
                <button
                  onClick={showHint}
                  className="hint-btn"
                  title="Get a hint"
                >
                  💡 Hint
                </button>
                {attempts >= 2 && !showSolution && (
                  <button
                    onClick={displaySolution}
                    className="solution-btn"
                    title="Show solution"
                  >
                    📖 Solution
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="output-console">
            {error ? (
              <div className="error-message">❌ {error}</div>
            ) : (
              <pre>{output || 'Output will appear here...'}</pre>
            )}
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="interpreter-footer">
        <span className="footer-info">
          Powered by Piston API • {supportedLanguages.length} languages supported
        </span>
        {challenge && (
          <span className="attempts-counter">
            Attempts: {attempts}
          </span>
        )}
      </div>
    </div>
  );
};

export default CodeInterpreter;
